import { submitCustomerSignupHandoff } from "@/utils/customerSignupHandoff";
import Vue from "vue";
import store from "@/stores/store";
import vuetify from "./plugins/vuetify";
import Router from "vue-router";
import * as VueGoogleMaps from 'vue2-google-maps';
import { setPerformanceStore } from "@/plugins/axios";

import "@/assets/css/tailwind.css";
import "@/assets/css/_base.scss";
import "@/plugins";

// Set the store for performance monitoring
setPerformanceStore(store);

// Configure Google Maps
// Simple obfuscation function to decode API key
const decodeKey = (encoded) => {
    const key = 'safetee-maps-2024';
    const decoded = atob(encoded);
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
};

const GOOGLE_MAPS_API_KEY = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || decodeKey('MigcBCccJGlYNyRBaQNAH0AJLz4EAhUtYkATNBxFRFNHUyw2OSkb');

// Load Google Maps script manually
function loadGoogleMapsScript() {
    return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&region=FR&language=fr`;
        script.async = true;
        script.defer = true;
        script.crossOrigin = 'anonymous';
        
        script.onload = () => {
            resolve();
        };
        
        script.onerror = (error) => {
            console.error('Failed to load Google Maps script:', error);
            reject(error);
        };
        
        document.head.appendChild(script);
    });
}

// Load Google Maps and then initialize Vue (do not pass `load` again — the
// script above already loaded Maps; a second Loader.provide races geocoder).
// Without `load`, vue2-google-maps waits for window.vueGoogleMapsInit. The
// script URL has no callback, so the map promise never resolves and GmapMap
// stays blank. Signal readiness once the API is already on the page.
loadGoogleMapsScript()
    .then(() => {
        Vue.use(VueGoogleMaps);
        if (typeof window.vueGoogleMapsInit === "function") {
            window.vueGoogleMapsInit();
        }
    })
    .catch((error) => {
        console.error('Failed to load Google Maps:', error);
    });

Vue.config.productionTip = false;
Vue.prototype.$isProduction = process.env.NODE_ENV === 'production';

Vue.use(Router);

Vue.mixin({
    methods: {
        routerPush: function (route) {
            return this.$router.push(route).catch((error) => {
                if (error.name != "NavigationDuplicated") {
                    throw error;
                }
            });
        },
        routerReplace: function (route) {
            return this.$router.replace(route).catch((error) => {
                if (error.name != "NavigationDuplicated") {
                    throw error;
                }
            });
        }
    },
});


const { canAccessOnboarding } = require("@/utils/onboardingPermissions");

const LIGHTSPEED_OAUTH_CALLBACK_PATH = '/api/connectors/pos/lightspeed/oauth/callback/';

function redirectLightspeedOAuthCallbackFromRoot() {
    const { pathname, search } = window.location;
    if (pathname.startsWith(LIGHTSPEED_OAUTH_CALLBACK_PATH)) {
        return false;
    }
    const params = new URLSearchParams(search);
    const state = params.get('state');
    if (!state) {
        return false;
    }
    if (!params.get('code') && !params.get('error')) {
        return false;
    }
    window.location.replace(`${LIGHTSPEED_OAUTH_CALLBACK_PATH}${search}`);
    return true;
}

async function loadConnectedApp() {
    const entity = store.getters["session/current_entity"];
    return {
        app: (await import("@/apps/connected")).default,
        router: (await import(`@/routers/${entity.profile}`)).default,
    };
}

function isPublicDeliveryConfirmPath() {
    return /^\/deliveries\/confirm\/[^/]+\/?$/.test(window.location.pathname);
}

async function init() {
    if (submitCustomerSignupHandoff()) return;
    if (isPublicDeliveryConfirmPath()) {
        const i18n = require("@/i18n/i18n").default;
        const app = (await import("@/apps/anonymous")).default;
        const router = (await import("@/routers/anonymous")).default;
        new Vue({
            router,
            store,
            vuetify,
            i18n,
            render: (h) => h(app),
        }).$mount("#app");
        return;
    }

    store
        .dispatch("session/fetchLoginInfo")
        .then(async () => {
            // Load locale settings (must be passed to root Vue for component i18n blocks)
            const i18n = require("@/i18n/i18n").default;

            let app;
            let router;

            // Determine which app and router to load based on the user state
            if (store.getters["session/current_user_connected"]) {
                let entity = store.getters["session/current_entity"];
                let onboarding_completed = store.getters["session/onboarding_completed"];
                const must_confirm_identity =
                    store.getters["session/current_user_must_confirm_identity"];

                if (!onboarding_completed) {
                    const permissions = store.getters["session/current_user_permissions"];
                    const readonly = store.getters["session/readonly"];
                    // Impersonators still complete company + warehouses, but skip
                    // email confirmation and the questionnaire inside onboarding.
                    if (canAccessOnboarding(permissions, { readonly })) {
                        app = (await import("@/apps/onboarding")).default;
                        router = (await import("@/routers/onboarding")).default;
                    } else if (must_confirm_identity) {
                        app = (await import("@/apps/unverified")).default;
                        router = (await import("@/routers/unverified")).default;
                    } else {
                        ({ app, router } = await loadConnectedApp());
                        if (window.location.pathname !== "/dashboard") {
                            window.history.replaceState({}, "", "/dashboard");
                        }
                    }
                } else if (must_confirm_identity) {
                    app = (await import("@/apps/unverified")).default;
                    router = (await import("@/routers/unverified")).default;
                } else {
                    ({ app, router } = await loadConnectedApp());
                }
            } else {
                app = (await import("@/apps/anonymous")).default;
                router = (await import("@/routers/anonymous")).default;
            }

            // Mount the Vue instance
            new Vue({
                router,
                store,
                vuetify,
                i18n,
                render: (h) => h(app),
            }).$mount("#app");

            if (store.getters["session/current_user_connected"]) {
                store.dispatch("session/startLoginInfoPolling");
                store.dispatch("inappNotifications/startPolling");
            }
        })
        .catch((error) => {
            console.log(error);
            alert("An error occurred while fetching your information, please try again");
        });
}

if (!redirectLightspeedOAuthCallbackFromRoot()) {
    init();
}
